/**
 * Customer Trip Detail Page
 *
 * Comprehensive view of a single trip with:
 * - Trip details and itinerary
 * - Guide contact information
 * - Location map
 * - Preparation checklist
 * - Booking modification options
 * - Payment information
 * - Cancellation policy
 *
 * Epic: MVP 1.21.4 - Customer Dashboard
 * Story: US-1.21.4-04: Trip Detail View (5 SP)
 * Story: US-1.21.4-05: Booking Modification (3 SP)
 *
 * Data Flow:
 * - Fetches from GET /api/v1/customer/trips/:bookingId
 * - PUT /api/v1/customer/bookings/:bookingId/modify
 * - POST /api/v1/customer/bookings/:bookingId/cancel
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiRequest from '../../services/api';
import {
  Calendar,
  MapPin as _MapPin,
  DollarSign,
  Star as _Star,
  Users as _Users,
  Clock,
  Phone,
  Mail as _Mail,
  MessageSquare,
  AlertCircle as _AlertCircle,
  Edit,
  XCircle,
  CheckCircle,
  Info,
  Download as _Download
} from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorAlert from '../../components/common/ErrorAlert';
import Avatar from '../../components/common/Avatar';
import { logError } from '../../services/errorLoggingService';
import { getGuidePhotoUrl } from '../../utils/guideDataAdapters';
import TripChecklist from '../../components/trips/TripChecklist';
import CancellationModal from '../../components/booking/CancellationModal';
import DisputeButton from '../../components/booking/DisputeButton';
import { exportToCalendar } from '../../utils/icsGenerator';

export default function CustomerTripDetail() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modification modal state
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [modifyData, setModifyData] = useState({ newDate: '', newParticipantCount: '' });
  const [modifying, setModifying] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchTripDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/api/v1/customer/trips/${bookingId}`, { method: 'GET' });
      if (response.success) {
        const tripData = response.data;
        setTrip(tripData);
        setModifyData({
          newDate: tripData.start_date?.split('T')[0] || '',
          newParticipantCount: tripData.guests || ''
        });
      }
    } catch (err) {
      logError(err, { componentName: 'CustomerTripDetail', action: 'fetch_trip' });
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to load trip details');
      }
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  // Fetch trip details
  useEffect(() => {
    fetchTripDetails();
  }, [fetchTripDetails]);

  // Handle booking modification
  const handleModifyBooking = async () => {
    try {
      setModifying(true);
      setErrorMessage(null);
      const response = await apiRequest(`/api/v1/customer/bookings/${bookingId}/modify`, {
        method: 'PUT',
        body: JSON.stringify({
          newDate: modifyData.newDate,
          newParticipantCount: parseInt(modifyData.newParticipantCount, 10)
        })
      });

      if (response.success) {
        await fetchTripDetails();
        setShowModifyModal(false);
        setSuccessMessage('Booking modified successfully!');
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } catch (err) {
      logError(err, { componentName: 'CustomerTripDetail', action: 'modify_booking' });
      if (err.status === 401) {
        setErrorMessage('Session expired. Please log in again.');
      } else {
        setErrorMessage(err.message || 'Failed to modify booking');
      }
      setTimeout(() => setErrorMessage(null), 5000);
    } finally {
      setModifying(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format time
  const formatTime = (timeString) => {
    if (!timeString) {return '';}
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Handle navigation
  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  if (loading) {
    return (
      <DashboardLayout userRole="customer" activeNavId="trips" onNavigate={handleNavigate}>
        <LoadingSpinner message="Loading trip details..." className="min-h-screen" />
      </DashboardLayout>
    );
  }

  if (error || !trip) {
    return (
      <DashboardLayout userRole="customer" activeNavId="trips" onNavigate={handleNavigate}>
        <ErrorAlert message={error || 'Trip not found'} />
      </DashboardLayout>
    );
  }

  const canModify = trip.status === 'confirmed' && new Date(trip.start_date) > new Date();
  const canCancel = ['confirmed', 'pending'].includes(trip.status) && new Date(trip.start_date) > new Date();

  return (
    <DashboardLayout
      userRole="customer"
      activeNavId="trips"
      onNavigate={handleNavigate}
    >
      <div className="space-y-6">
        {/* Success/Error Messages */}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg flex items-center space-x-2">
            <CheckCircle className="w-5 h-5" />
            <span>{successMessage}</span>
          </div>
        )}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg flex items-center space-x-2">
            <_AlertCircle className="w-5 h-5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/customer/trips')}
              className="text-sm text-gray-600 hover:text-gray-900 mb-2"
            >
              ← Back to Trips
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{trip.experience_title}</h1>
            {/* SCRUM-1123 FIX (2026-03-16): Guard against undefined before .slice()
              *
              * booking_id can be undefined when the API response omits it for certain
              * booking types or when the response shape differs from expected. Without
              * this guard, the component throws TypeError: Cannot read properties of
              * undefined (reading 'slice'), crashing the page for every trip detail visit.
              * Fallback to empty string so downstream rendering continues safely. */}
            <p className="mt-1 text-sm text-gray-500">
              Booking #{(trip.booking_id || '').slice(0, 8)}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {canModify && (
              <button
                onClick={() => setShowModifyModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>Modify</span>
              </button>
            )}
            {canCancel && (
              <button
                onClick={() => setShowCancelModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            )}
          </div>
          {/* SCRUM-1202: Display cancellation deadline from API response */}
          {canCancel && trip.cancellationDeadline && (
            <p className="text-sm text-gray-500 mt-1">
              Cancel by {formatDate(trip.cancellationDeadline)}
            </p>
          )}
        </div>

        {/* Status Banner */}
        <div className={`p-4 rounded-lg border ${
          trip.status === 'confirmed' ? 'bg-green-50 border-green-200' :
          trip.status === 'pending' ? 'bg-yellow-50 border-yellow-200' :
          trip.status === 'completed' ? 'bg-blue-50 border-blue-200' :
          'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center space-x-2">
            {trip.status === 'confirmed' && <CheckCircle className="w-5 h-5 text-green-600" />}
            {trip.status === 'pending' && <Clock className="w-5 h-5 text-yellow-600" />}
            {trip.status === 'completed' && <CheckCircle className="w-5 h-5 text-blue-600" />}
            {trip.status === 'cancelled' && <XCircle className="w-5 h-5 text-red-600" />}
            <p className={`text-sm font-medium ${
              trip.status === 'confirmed' ? 'text-green-800' :
              trip.status === 'pending' ? 'text-yellow-800' :
              trip.status === 'completed' ? 'text-blue-800' :
              'text-red-800'
            }`}>
              {trip.status === 'confirmed' && 'Your trip is confirmed! Get ready for your adventure.'}
              {trip.status === 'pending' && 'Payment pending. Please complete payment to confirm your trip.'}
              {trip.status === 'completed' && 'Trip completed. Thank you for booking with us!'}
              {trip.status === 'cancelled' && 'This trip has been cancelled.'}
            </p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Trip Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Trip Information */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Trip Details</h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Date</p>
                    <p className="font-medium text-gray-900">{formatDate(trip.start_date)}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Time</p>
                    <p className="font-medium text-gray-900">{formatTime(trip.trip_time) || 'TBD'}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <_Users className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Guests</p>
                    <p className="font-medium text-gray-900">{trip.guests}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <_MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-600">Location</p>
                    <p className="font-medium text-gray-900">{trip.location}</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Experience Description */}
            {trip.experience_description && (
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">About This Experience</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {trip.experience_description}
                </p>
              </Card>
            )}

            {/* What&apos;s Included */}
            {trip.whats_included && trip.whats_included.length > 0 && (
              <Card>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">What&apos;s Included</h2>
                <ul className="space-y-2">
                  {trip.whats_included.map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Preparation Checklist - MVP 1.21.5 Story 4.1 */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Preparation Checklist</h2>
              <TripChecklist bookingId={bookingId} />
            </Card>
          </div>

          {/* Right Column - Guide & Payment */}
          <div className="space-y-6">
            {/* Guide Information */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Your Guide</h2>
              <div className="flex items-center space-x-4 mb-4">
                <Avatar
                  src={getGuidePhotoUrl({ profile_photo_url: trip.guide_photo, profilePhoto: trip.guideProfilePhoto }, 'profile', 'small')}
                  name={trip.guide_name}
                  size="xl"
                />
                <div>
                  <h3 className="font-semibold text-gray-900">{trip.guide_name}</h3>
                  {trip.guide_rating && (
                    <div className="flex items-center space-x-1 mt-1">
                      <_Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm font-medium">{trip.guide_rating.toFixed(1)}</span>
                      <span className="text-sm text-gray-500">({trip.guide_reviews || 0} reviews)</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  fullWidth
                  onClick={() => navigate(`/customer/messages?guide=${trip.guide_id}`)}
                >
                  <MessageSquare className="w-4 h-4" />
                  <span>Message Guide</span>
                </Button>

                {/* MVP 1.21.5 Story 4.2: Add to Calendar */}
                <Button
                  variant="secondary"
                  fullWidth
                  onClick={() => exportToCalendar(trip, 'download')}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Add to Calendar</span>
                </Button>

                {trip.guide_phone && (
                  <a
                    href={`tel:${trip.guide_phone}`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    <span>{trip.guide_phone}</span>
                  </a>
                )}
              </div>
            </Card>

            {/* Payment Information */}
            <Card>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(trip.subtotal)}</span>
                </div>
                {trip.discount > 0 && (
                  <div className="flex items-center justify-between text-green-600">
                    <span>Discount</span>
                    <span className="font-medium">-{formatCurrency(trip.discount)}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Service Fee</span>
                  <span className="font-medium">{formatCurrency(trip.service_fee)}</span>
                </div>
                <div className="pt-3 border-t border-gray-200 flex items-center justify-between">
                  <span className="font-semibold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-gray-900">{formatCurrency(trip.total_price)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Paid</span>
                  <span className="font-medium text-green-600">{formatCurrency(trip.amount_paid)}</span>
                </div>
                {trip.balance_due > 0 && (
                  <div className="flex items-center justify-between text-orange-600">
                    <span className="font-semibold">Balance Due</span>
                    <span className="text-lg font-bold">{formatCurrency(trip.balance_due)}</span>
                  </div>
                )}
              </div>

              {trip.balance_due > 0 && (
                <button
                  onClick={() => navigate(`/customer/bookings/${trip.booking_id}/payment`)}
                  className="w-full mt-4 flex items-center justify-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                >
                  <DollarSign className="w-4 h-4" />
                  <span>Pay Balance</span>
                </button>
              )}
            </Card>

            {/* Cancellation Policy */}
            {trip.cancellation_policy && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Cancellation Policy</p>
                    <p>{trip.cancellation_policy}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modify Booking Modal */}
        {showModifyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Modify Booking</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trip Date
                  </label>
                  <input
                    type="date"
                    value={modifyData.newDate}
                    onChange={(e) => setModifyData({ ...modifyData, newDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Guests
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={modifyData.newParticipantCount}
                    onChange={(e) => setModifyData({ ...modifyData, newParticipantCount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div className="flex items-center space-x-3 pt-4">
                  <button
                    onClick={handleModifyBooking}
                    disabled={modifying}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {modifying ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setShowModifyModal(false)}
                    className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cancel Booking Modal - Phase 3.2B multi-step flow */}
        <CancellationModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          bookingId={bookingId}
          onCancelled={() => fetchTripDetails()}
        />

        {/* Dispute Button - shows when a cancellation decision exists */}
        {trip.cancellation_id && trip.status === 'cancelled' && (
          <DisputeButton
            bookingId={bookingId}
            cancellationId={trip.cancellation_id}
          />
        )}
      </div>
    </DashboardLayout>
  );
}
